import { FEATURED_BRANDS } from "./brand-logos";
import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";


import {
    SearchClientFactory,
    SearchConfig,
} from "../services/SearchClientFactory";
import { resolveSearchPanelLiantOptions } from "./pill-search-panel-liant";
import { installAutocompleteScrollGuard } from "./autocomplete-scroll-guard";
import { markSearchControls, markSearchPanel } from "../marketplace/listing-tracking";
import { rememberSearchFiltersOrigin } from "./search-filters-origin";

const brandData = FEATURED_BRANDS;

export interface AutocompleteMountOptions {
    containerId: string;
    searchConfig: SearchConfig | null;
    placeholder?: string;
    recentSearchesKey?: string;
}

export function redirectToMarketplace(params: {
    query?: string;
    make?: string;
}): void {
    const urlParams = new URLSearchParams();
    if (params.query) {
        rememberSearchFiltersOrigin(params.query);
        urlParams.set("query", params.query);
    }
    if (params.make) {
        urlParams.set("menu[make]", params.make);
    }
    const queryString = urlParams.toString();
    window.location.href = `/marketplace${
        queryString ? `?${queryString}` : ""
    }`;
}

function hasWordStartingWith(text: string, query: string): boolean {
    if (!text || !query) return false;

    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();

    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        return words.some((word) => word.startsWith(queryLower));
    }

    const queryWords = queryLower.split(/\s+/);
    const textWords = textLower.split(/\s+/);

    for (let i = 0; i <= textWords.length - queryWords.length; i++) {
        let allMatch = true;
        for (let j = 0; j < queryWords.length; j++) {
            const textWord = textWords[i + j];
            const queryWord = queryWords[j];
            if (j === 0) {
                if (
                    !textWord.includes(queryWord) &&
                    !textWord.startsWith(queryWord)
                ) {
                    allMatch = false;
                    break;
                }
            } else {
                if (!textWord.startsWith(queryWord)) {
                    allMatch = false;
                    break;
                }
            }
        }
        if (allMatch) return true;
    }
    return false;
}

function findWordMatch(
    text: string,
    query: string,
): { start: number; end: number } | null {
    if (!text || !query) return null;

    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();

    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        let currentIndex = 0;
        for (const word of words) {
            const wordStartIndex = textLower.indexOf(word, currentIndex);
            if (word.startsWith(queryLower)) {
                return {
                    start: wordStartIndex,
                    end: wordStartIndex + queryLower.length,
                };
            }
            currentIndex = wordStartIndex + word.length;
        }
        return null;
    }

    const queryWords = queryLower.split(/\s+/);
    const firstQueryWord = queryWords[0];
    const firstWordIndex = textLower.indexOf(firstQueryWord);
    if (firstWordIndex === -1) return null;

    const queryEndIndex = firstWordIndex + queryLower.length;
    return {
        start: firstWordIndex,
        end: Math.min(queryEndIndex, textLower.length),
    };
}

function getShortestMatchingWordLength(text: string, query: string): number {
    if (!text || !query) return Infinity;

    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();

    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        let shortestLength = Infinity;
        for (const word of words) {
            if (word.startsWith(queryLower)) {
                shortestLength = Math.min(shortestLength, word.length);
            }
        }
        return shortestLength;
    }

    if (hasWordStartingWith(textLower, queryLower)) {
        return queryLower.length;
    }
    return Infinity;
}

function createQuerySuggestionsPlugin(
    autocompleteSearchClient: any,
    indexName: string,
) {
    return {
        getSources({ query }: { query: string }) {
            if (!query) {
                return [];
            }

            return [
                {
                    sourceId: "querySuggestionsPlugin",
                    async getItems() {
                        try {
                            const results =
                                await autocompleteSearchClient.search([
                                    {
                                        indexName: indexName,
                                        params: {
                                            query: query,
                                            hitsPerPage: 50,
                                            attributesToRetrieve: [
                                                "suggestion_make",
                                                "suggestion_make_model",
                                                "suggestion_make_model_specification",
                                            ],
                                        },
                                    },
                                ]);

                            const suggestions: Array<{ query: string }> = [];
                            const seenQueries = new Set<string>();
                            const queryLower = query.toLowerCase();

                            const makeSuggestions: Array<{ query: string }> =
                                [];
                            const makeModelSuggestions: Array<{
                                query: string;
                            }> = [];
                            const makeModelSpecificationSuggestions: Array<{
                                query: string;
                            }> = [];

                            results.results[0].hits.forEach((hit: any) => {
                                if (
                                    hit.suggestion_make &&
                                    !seenQueries.has(hit.suggestion_make) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make,
                                        queryLower,
                                    )
                                ) {
                                    makeSuggestions.push({
                                        query: hit.suggestion_make,
                                    });
                                    seenQueries.add(hit.suggestion_make);
                                }

                                if (
                                    hit.suggestion_make_model &&
                                    !seenQueries.has(
                                        hit.suggestion_make_model,
                                    ) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSuggestions.push({
                                        query: hit.suggestion_make_model,
                                    });
                                    seenQueries.add(hit.suggestion_make_model);
                                }

                                if (
                                    hit.suggestion_make_model_specification &&
                                    !seenQueries.has(
                                        hit.suggestion_make_model_specification,
                                    ) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model_specification,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSpecificationSuggestions.push({
                                        query: hit.suggestion_make_model_specification,
                                    });
                                    seenQueries.add(
                                        hit.suggestion_make_model_specification,
                                    );
                                }
                            });

                            const sortByMatchLength = (
                                items: Array<{ query: string }>,
                            ) => {
                                return items.sort((a, b) => {
                                    const lengthA =
                                        getShortestMatchingWordLength(
                                            a.query,
                                            queryLower,
                                        );
                                    const lengthB =
                                        getShortestMatchingWordLength(
                                            b.query,
                                            queryLower,
                                        );
                                    if (lengthA !== lengthB)
                                        return lengthA - lengthB;
                                    return a.query.localeCompare(b.query);
                                });
                            };

                            suggestions.push(
                                ...sortByMatchLength([...makeSuggestions]),
                            );
                            suggestions.push(
                                ...sortByMatchLength([...makeModelSuggestions]),
                            );
                            suggestions.push(
                                ...sortByMatchLength([
                                    ...makeModelSpecificationSuggestions,
                                ]),
                            );
                            suggestions.splice(7);

                            return suggestions;
                        } catch (error) {
                            console.warn("Error fetching suggestions:", error);
                            return [];
                        }
                    },
                    onSelect({ item }: { item: any }) {
                        redirectToMarketplace({ query: item.query });
                    },
                    templates: {
                        item({
                            item,
                            html,
                            state,
                        }: {
                            item: any;
                            html: any;
                            state: any;
                        }) {
                            const formattedQuery = item.query;
                            const userInput = (state.query || "").toLowerCase();

                            const selectSuggestion = () =>
                                redirectToMarketplace({ query: item.query });

                            if (!userInput) {
                                return html`<span
                                    class="aa-ItemContentTitle"
                                    onclick=${selectSuggestion}
                                    >${formattedQuery}</span
                                >`;
                            }

                            const wordMatch = findWordMatch(
                                formattedQuery,
                                userInput,
                            );
                            if (!wordMatch) {
                                return html`<span
                                    class="aa-ItemContentTitle"
                                    onclick=${selectSuggestion}
                                    >${formattedQuery}</span
                                >`;
                            }

                            const beforeMatch = formattedQuery.substring(
                                0,
                                wordMatch.start,
                            );
                            const match = formattedQuery.substring(
                                wordMatch.start,
                                wordMatch.end,
                            );
                            const afterMatch = formattedQuery.substring(
                                wordMatch.end,
                            );

                            return html`<span
                                class="aa-ItemContentTitle"
                                onclick=${selectSuggestion}
                            >
                                ${beforeMatch}<mark>${match}</mark>${afterMatch}
                            </span>`;
                        },
                    },
                },
            ];
        },
    };
}

function createRecentSearchesPlugin(
    recentSearchesKey: string,
) {
    return createLocalStorageRecentSearchesPlugin({
        key: recentSearchesKey,
        limit: 3,
        transformSource({ source }) {
            return {
                ...source,
                getItems(params) {
                    if (params.query && params.query.length > 0) {
                        return [];
                    }
                    return source.getItems(params);
                },
                onSelect({ item }) {
                    redirectToMarketplace({ query: item.label });
                },
                templates: {
                    ...source.templates,
                    item(params: any) {
                        const { item, html } = params;
                        return html`<span
                            class="aa-ItemLink"
                            onclick=${() =>
                                redirectToMarketplace({ query: item.label })}
                            >${item.label}</span
                        >`;
                    },
                },
            };
        },
    });
}

function getRecentSearchesCount(recentSearchesPlugin: any): number {
    if (recentSearchesPlugin.data) {
        const recentSearches = recentSearchesPlugin.data.getAll();
        return Array.isArray(recentSearches) ? recentSearches.length : 0;
    }
    return 0;
}

export function createAutocompleteInstance(
    options: AutocompleteMountOptions,
): ReturnType<typeof autocomplete> | null {
    const {
        containerId,
        searchConfig,
        placeholder = "",
        recentSearchesKey = `instantsearch-${containerId}`,
    } = options;

    const container = document.getElementById(containerId);
    if (!container) {
        return null;
    }

    installAutocompleteScrollGuard();

    const recentSearchesPlugin = createRecentSearchesPlugin(recentSearchesKey);
    const plugins: any[] = [recentSearchesPlugin];

    if (searchConfig?.driver === "typesense") {
        try {
            const autocompleteSearchClient =
                SearchClientFactory.createAutocompleteClient(searchConfig);
            plugins.push(
                createQuerySuggestionsPlugin(
                    autocompleteSearchClient,
                    searchConfig.indexName,
                ),
            );
        } catch (error) {
            console.warn("Failed to create autocomplete search client:", error);
        }
    }

    const panelLiant = resolveSearchPanelLiantOptions(containerId);
    // Repères PostHog : recherche de l'en-tête ou recherche de la page (accueil, landings)
    const trackingPrefix = container.closest("header") ? "header-search" : "hero-search";

    container
        .querySelector(".autocomplete__home-placeholder")
        ?.remove();

    const instance = autocomplete({
        container: `#${containerId}`,
        placeholder,
        detachedMediaQuery: "none",
        openOnFocus: true,
        ...(panelLiant ?? {}),
        plugins,
        onSubmit({ state }) {
            redirectToMarketplace({ query: state.query });
        },
        onReset() {
            // Nothing to do on reset
        },
        getSources(): any {
            return [
                {
                    sourceId: "brands",
                    getItems({ query }: any) {
                        if (query && query.length > 0) {
                            return [];
                        }
                        return brandData.filter(({ label }) =>
                            label
                                .toLowerCase()
                                .includes((query || "").toLowerCase()),
                        );
                    },
                    onSelect({ item }: any) {
                        redirectToMarketplace({ make: item.label });
                    },
                    templates: {
                        item({ item, html }: any) {
                            return html`<button
                                type="button"
                                class="aa-ItemLink"
                                onclick=${() =>
                                    redirectToMarketplace({ make: item.label })}
                            >
                                <img
                                    class="aa-Item--icon"
                                    src="${item.img}"
                                    alt="${item.label}"
                                />
                                ${item.label}
                            </button>`;
                        },
                    },
                },
            ];
        },
        render({ elements, render, html, state }: any, root: any) {
            // Repère PostHog du panneau : toute suggestion cliquée dedans le porte (docs/tracking-data-attr.md)
            markSearchPanel(root, `${trackingPrefix}-suggestion`);
            const {
                recentSearchesPlugin: recentEl,
                querySuggestionsPlugin: suggestionsEl,
                brands,
            } = elements;
            const recentSearchesCount =
                getRecentSearchesCount(recentSearchesPlugin);

            if (!state.query) {
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            ${recentSearchesCount > 0
                                ? html`<div>
                                      <div class="aa-SourceHeader">
                                          <span class="aa-SourceHeaderTitle">
                                              Recherches récentes
                                          </span>
                                      </div>
                                      ${recentEl}
                                  </div>`
                                : ""}
                            <div>
                                <div class="aa-SourceHeader">
                                    <span class="aa-SourceHeaderTitle"
                                        >Marques emblématiques</span
                                    >
                                </div>
                                <div class="aa-PanelSectionBrands">
                                    ${brands}
                                </div>
                            </div>
                        </div>
                    </div>` as any,
                    root,
                );
            } else {
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            <div>${suggestionsEl || ""}</div>
                        </div>
                    </div>` as any,
                    root,
                );
            }
        },
    });

    // Contrôles rendus par la bibliothèque, sans gabarit : repérés après le montage
    markSearchControls(container, trackingPrefix);

    return instance;
}
